import { Autocomplete, CircularProgress, Stack, TextField, Typography } from '@mui/material';
import { useCalculusTFunction } from '@retail/calculus/i18n';
import { useMemo } from 'react';

export type ConditionTarget = { type: 'assortment' } | { type: 'supplier'; supplierNumber: number };

interface Supplier {
  supplierNumber: number;
  name: string;
}

type Option = { type: 'assortment' } | ({ type: 'supplier' } & Supplier);

interface Props {
  suppliers: Supplier[];
  value: ConditionTarget | null;
  onChange: (target: ConditionTarget | null) => void;
  isLoading?: boolean;
}

const assortmentOption: Option = { type: 'assortment' };

const isSameOption = (a: Option | ConditionTarget, b: Option | ConditionTarget) =>
  a.type === b.type &&
  (a.type === 'assortment' ||
    a.supplierNumber === (b as { supplierNumber: number }).supplierNumber);

export function ConditionTargetSelect({ suppliers, value, onChange, isLoading }: Props) {
  const t = useCalculusTFunction();

  const options = useMemo<Option[]>(
    () => [
      assortmentOption,
      ...suppliers.map((supplier) => ({ type: 'supplier' as const, ...supplier })),
    ],
    [suppliers]
  );

  const getOptionLabel = (option: Option) =>
    option.type === 'assortment'
      ? t('calculus.level.assortment')
      : `${option.name} ${option.supplierNumber}`;

  return (
    <Stack gap={0.5} pb={2}>
      <Typography
        component="label"
        htmlFor="conditionTargetSelect"
        variant="body2"
        color="text.secondary"
      >
        {t('calculus.level.conditionTarget.label')}
      </Typography>
      <Autocomplete
        id="conditionTargetSelect"
        size="small"
        options={options}
        value={value ? options.find((option) => isSameOption(option, value)) ?? null : null}
        onChange={(_, option) =>
          onChange(
            option
              ? option.type === 'assortment'
                ? { type: 'assortment' }
                : { type: 'supplier', supplierNumber: option.supplierNumber }
              : null
          )
        }
        isOptionEqualToValue={isSameOption}
        getOptionLabel={getOptionLabel}
        disabled={isLoading}
        renderInput={(params) => (
          <TextField
            {...params}
            placeholder={t('calculus.level.conditionTarget.placeholder')}
            slotProps={{
              input: {
                ...params.InputProps,
                endAdornment: (
                  <>
                    {isLoading ? <CircularProgress color="inherit" size={20} /> : null}
                    {params.InputProps.endAdornment}
                  </>
                ),
              },
            }}
          />
        )}
      />
    </Stack>
  );
}
